import { useEffect, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import "./Maintenance.scss";

const getTimeUnitLabel = (value, unit, language) => {
  const pluralRules = {
    ua: {
      days: ['день', 'дні', 'днів'],
      hours: ['година', 'години', 'годин'],
      minutes: ['хвилина', 'хвилини', 'хвилин'],
      seconds: ['секунда', 'секунди', 'секунд']
    },
    en: {
      days: ['day', 'days', 'days'],
      hours: ['hour', 'hours', 'hours'],
      minutes: ['minute', 'minutes', 'minutes'],
      seconds: ['second', 'seconds', 'seconds']
    },
    ru: {
      days: ['день', 'дня', 'дней'],
      hours: ['час', 'часа', 'часов'],
      minutes: ['минута', 'минуты', 'минут'],
      seconds: ['секунда', 'секунды', 'секунд']
    },
    pl: {
      days: ['dzień', 'dni', 'dni'],
      hours: ['godzina', 'godziny', 'godzin'],
      minutes: ['minuta', 'minuty', 'minut'],
      seconds: ['sekunda', 'sekundy', 'sekund']
    },
    de: {
      days: ['Tag', 'Tage', 'Tagen'],
      hours: ['Stunde', 'Stunden', 'Stunden'],
      minutes: ['Minute', 'Minuten', 'Minuten'],
      seconds: ['Sekunde', 'Sekunden', 'Sekunden']
    },
    es: {
      days: ['día', 'días', 'días'],
      hours: ['hora', 'horas', 'horas'],
      minutes: ['minuto', 'minutos', 'minutos'],
      seconds: ['segundo', 'segundos', 'segundos']
    }
  };

  const rules = pluralRules[language] || pluralRules.en;
  const forms = rules[unit] || rules.days;

  const accusativeForms = {
    ua: {
      days: 'день',
      hours: 'годину',
      minutes: 'хвилину',
      seconds: 'секунду'
    },
    ru: {
      days: 'день',
      hours: 'час',
      minutes: 'минуту',
      seconds: 'секунду'
    }
  };

  if (['en', 'de', 'es'].includes(language)) {
    return value === 1 ? forms[0] : forms[1];
  }

  if (language === 'pl') {
    if (value === 1) return forms[0];
    const lastDigit = value % 10;
    const lastTwoDigits = value % 100;
    if ((lastDigit >= 2 && lastDigit <= 4) && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)) {
      return forms[1];
    }
    return forms[2];
  }

  const lastDigit = value % 10;
  const lastTwoDigits = value % 100;

  if (lastDigit === 1 && lastTwoDigits !== 11) {
    const accusative = accusativeForms[language] || accusativeForms.en;
    return accusative[unit] || forms[0];
  }

  if ([2, 3, 4].includes(lastDigit) && ![12, 13, 14].includes(lastTwoDigits)) {
    return forms[1];
  }

  return forms[2];
};

const MaintenanceWrapper = ({ maintenanceMode, maintenanceEndTime, children }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const staticData = {
    errorTitle: {
      ua: '503',
      en: '503',
      ru: '503',
      pl: '503',
      de: '503',
      es: '503',
    },
    errorSubtitle: {
      ua: 'Ведуться роботи на сервері!',
      en: 'Server Maintenance!',
      ru: 'Проводятся работы на сервере!',
      pl: 'Trwają prace serwerowe!',
      de: 'Serverwartung!',
      es: '¡Mantenimiento del servidor!',
    },
    errorText: {
      ua: 'Зараз ми оновлюємо дані та покращуємо роботу сайту. Сторінка буде доступна через:',
      en: 'We are updating data and improving the website. The page will be available in:',
      ru: 'Сейчас мы обновляем данные и улучшаем работу сайта. Страница будет доступна через:',
      pl: 'Aktualizujemy dane i usprawniamy działanie strony. Strona będzie dostępna za:',
      de: 'Wir aktualisieren gerade Daten und verbessern die Website. Die Seite wird verfügbar sein in:',
      es: 'Estamos actualizando datos y mejorando el sitio web. La página estará disponible en:',
    },
  };

  const getTimeLeft = useCallback(() => {
    if (!maintenanceEndTime) return 0;
    const diff = maintenanceEndTime - Date.now();
    return diff > 0 ? diff : 0;
  }, [maintenanceEndTime]);

  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [getTimeLeft]);

  useEffect(() => {
    if (maintenanceMode && maintenanceEndTime) {
      localStorage.setItem('maintenance_end_time', maintenanceEndTime.toString());
    } else if (!maintenanceMode) {
      localStorage.removeItem('maintenance_end_time');
    }
  }, [maintenanceMode, maintenanceEndTime]);

  useEffect(() => {
    if (maintenanceMode && maintenanceEndTime && timeLeft === 0) {
      const now = Date.now();
      const endTime = new Date(maintenanceEndTime).getTime();

      if (now >= endTime) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏰ Maintenance mode: reloading page');
        }

        if (!window._isReloading) {
          window._isReloading = true;
          const timer = setTimeout(() => {
            window.location.reload();
          }, 1000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [maintenanceMode, maintenanceEndTime, timeLeft]);

  useEffect(() => {
    if (maintenanceMode) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [maintenanceMode]);

  const getTimeUnits = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      days: {
        value: days,
        label: getTimeUnitLabel(days, 'days', currentLang)
      },
      hours: {
        value: hours,
        label: getTimeUnitLabel(hours, 'hours', currentLang)
      },
      minutes: {
        value: minutes,
        label: getTimeUnitLabel(minutes, 'minutes', currentLang)
      },
      seconds: {
        value: seconds,
        label: getTimeUnitLabel(seconds, 'seconds', currentLang)
      },
    };
  };

  const formatTime = (ms) => {
    if (ms <= 0) return '0 ' + getTimeUnitLabel(0, 'seconds', currentLang);

    const timeUnits = getTimeUnits(ms);
    const parts = [];

    if (timeUnits.days.value > 0) {
      parts.push(`${timeUnits.days.value} ${timeUnits.days.label}`);
    }
    if (timeUnits.hours.value > 0) {
      parts.push(`${timeUnits.hours.value} ${timeUnits.hours.label}`);
    }
    if (timeUnits.minutes.value > 0) {
      parts.push(`${timeUnits.minutes.value} ${timeUnits.minutes.label}`);
    }
    if (timeUnits.seconds.value >= 0) {
      parts.push(`${timeUnits.seconds.value} ${timeUnits.seconds.label}`);
    }

    return parts.join(' ');
  };

  if (!maintenanceMode) return <>{children}</>;

  return (
    <div className="maintenance-wrapper">
      <div className="maintenance-overlay">
        <div className="wrapper__503">
          <h5 className='error__503__title'>
            {staticData.errorTitle?.[currentLang]?.split("")?.map((digit, index) => (
              <span key={index} className={`fall-digit delay-${index}`}>
                {digit}
              </span>
            ))}
          </h5>
          <p className='error__503__subtitle'>
            {staticData.errorSubtitle?.[currentLang] || staticData.errorSubtitle?.en}
          </p>
          <p className='error__503__text'>
            {staticData.errorText?.[currentLang] || staticData.errorText?.en}
          </p>
          <p className="maintenance__timer">
            {timeLeft === 0 ? '0 ' + getTimeUnitLabel(0, 'seconds', currentLang) : formatTime(timeLeft)}
          </p>
        </div>
      </div>
      <div className="maintenance-content-blur">
        {children}
      </div>
    </div>
  );
};

export default MaintenanceWrapper;